/**
 * StanceSystem.ts — Five-state stance model for tactical combat.
 *
 * Modeled on RotaryKnights' positional ladder:
 * Pressing > Advantage > Neutral > Defensive > Cornered
 *
 * Each stance provides attack/defense/flee modifiers.
 * Stance shifts are determined by the action matchup each round.
 */

import type { CombatStance, StanceModifiers } from './CombatTypes';

// ─── Stance Ladder ──────────────────────────────────────────────

const STANCE_ORDER: CombatStance[] = [
  'cornered',    // 0 — worst
  'defensive',   // 1
  'neutral',     // 2 — starting
  'advantage',   // 3
  'pressing',    // 4 — best
];

const STANCE_MODIFIERS: Record<CombatStance, StanceModifiers> = {
  pressing:   { attack: +4, defense: +1, flee: -3, narrative: 'Full attack power — press the advantage!' },
  advantage:  { attack: +2, defense: +1, flee: -1, narrative: 'Good positioning — favorable ground.' },
  neutral:    { attack:  0, defense:  0, flee:  0, narrative: 'Even footing — no edge.' },
  defensive:  { attack: -2, defense: +2, flee: +1, narrative: 'Holding ground — find an opening.' },
  cornered:   { attack: -3, defense: -2, flee: +3, narrative: 'Backs against the wall — desperate!' },
};

// ─── Public API ─────────────────────────────────────────────────

/** Get the modifiers for a given stance. */
export function getStanceModifiers(stance: CombatStance): StanceModifiers {
  return STANCE_MODIFIERS[stance];
}

/** Get the initial stance for combat start. */
export function getInitialStance(): CombatStance {
  return 'neutral';
}

/** Get all stances in order from worst to best. */
export function getStanceOrder(): readonly CombatStance[] {
  return STANCE_ORDER;
}

/** Get the numeric index of a stance (0=cornered, 4=pressing). */
export function getStanceIndex(stance: CombatStance): number {
  return STANCE_ORDER.indexOf(stance);
}

/**
 * Shift a stance by the given amount (positive = improve, negative = worsen).
 * Clamps to cornered..pressing range.
 */
export function shiftStance(current: CombatStance, shift: number): CombatStance {
  const idx = STANCE_ORDER.indexOf(current);
  const newIdx = Math.max(0, Math.min(STANCE_ORDER.length - 1, idx + shift));
  return STANCE_ORDER[newIdx];
}

/** Get the narrative description for a stance. */
export function getStanceNarrative(stance: CombatStance): string {
  return STANCE_MODIFIERS[stance].narrative;
}

/**
 * Calculate stance shift from action matchup.
 * Rock-paper-scissors: attack beats flee, defend beats attack, ability is situational.
 * Returns positive for player advantage, negative for enemy advantage.
 */
export function calculateStanceShift(
  playerAction: 'attack' | 'defend' | 'ability' | 'item' | 'flee',
  enemyAction: 'attack' | 'defend' | 'ability' | 'item' | 'flee',
): number {
  // Simplified matchup matrix
  if (playerAction === enemyAction) return 0;

  // Attack vs Defend: defender gains advantage
  if (playerAction === 'attack' && enemyAction === 'defend') return -1;
  if (playerAction === 'defend' && enemyAction === 'attack') return +1;

  // Attack vs Flee: attacker gains advantage
  if (playerAction === 'attack' && enemyAction === 'flee') return +1;
  if (playerAction === 'flee' && enemyAction === 'attack') return -1;

  // Defend vs Flee: no tactical shift
  if (playerAction === 'defend' && enemyAction === 'flee') return 0;
  if (playerAction === 'flee' && enemyAction === 'defend') return 0;

  // Ability: small player advantage (abilities are proactive)
  if (playerAction === 'ability') return +1;
  if (enemyAction === 'ability') return -1;

  // Item: neutral
  return 0;
}
